import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState
} from "react"
import { SnakeCasedProperties } from "type-fest"
import {
  NoteFragment,
  NoteInput,
  PathItemFragment,
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useUpdateNoteMutation
} from "@pathwright/graphql-client/src/generated"
import {
  PathFromItems,
  createPathFromItems,
  unnestPathItems
} from "../../path/utils"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { convertObjectKeysToSnakeCase } from "../../utils/convertObjectKeys"
import useGroupedNotesByTarget, {
  GroupedNotesByTarget
} from "../useGroupedNotesbyTarget"
import useNotePermissions, { NotePermissions } from "../useNotePermissions"
import useNotes, { ContextInput } from "../useNotes"

type UseNotesReturnType = ReturnType<typeof useNotes>

export type NoteInputContext = SnakeCasedProperties<ContextInput>

export type NoteContextValue = {
  context: NoteInputContext
  // Currently selected (via noteSelection) or scoped (via pathItemSourceId prop) path item, if any.
  pathItem: PathFromItems<PathItemFragment>[number] | null
  // Nested path items.
  pathItems: PathFromItems<PathItemFragment>
  // Flattened path items for easier lookup.
  flattenedPathItems: PathFromItems<PathItemFragment>[number][]
  pathItemSourceId: number | null
  groupedNotesByTarget: GroupedNotesByTarget
  // Current note based on selection state.
  note: NoteFragment | null
  notes: UseNotesReturnType["notes"]
  notesQuery: UseNotesReturnType["query"]
  loadMore: UseNotesReturnType["loadMore"]
  hasMore: UseNotesReturnType["hasMore"]
  loadingMore: UseNotesReturnType["loadingMore"]
  noteSelection: NoteSelectionState
  setNoteSelection: Dispatch<NoteSelectionState>
  expandedNoteIds: Set<number>
  setExpandedNoteIds: Dispatch<SetStateAction<Set<number>>>
  confirmSelection: (noteInput?: NoteInput) => Promise<void>
  notePermissions: NotePermissions
}

export const NoteContext = createContext<NoteContextValue>(
  {} as NoteContextValue
)

export const useNoteContext = () => useContext(NoteContext)

export type NoteContextProviderProps = {
  context: ContextInput
  // Path items can be provided either as a flat array or as a nested structure.
  // The note context provider will convert flat arrays into a nested structure.
  pathItems: PathItemFragment[] | PathFromItems<PathItemFragment>
  // Optionally scope to a specific path item. This will not scope the notes query,
  // (you'd need to handle that via the ContextInput, if necessary), but rather, this will scope
  // the rendered list to the specified path item.
  pathItemSourceId?: number | null
  onCreateNote?: (note: NoteFragment) => void
  onUpdateNote?: (note: NoteFragment) => void
  onDeleteNote?: (note: NoteFragment) => void
  skipNotes?: boolean
  children: React.ReactNode
}

type NoteSelectionState =
  | {
      action: "create"
      itemId: number // referring to the path item source ID
    }
  | {
      action: "update" | "delete"
      noteId: number
    }
  | {
      action: "none"
    }

function reducer(
  state: NoteSelectionState,
  action: NoteSelectionState
): NoteSelectionState {
  switch (action.action) {
    case "create":
      return { action: "create", itemId: action.itemId }
    case "delete":
      return { action: "delete", noteId: action.noteId }
    case "update":
      return { action: "update", noteId: action.noteId }
    case "none":
      return { action: "none" }
    default:
      exhaustiveGuard(action)
  }
}

export function NoteContextProvider({
  context,
  pathItems,
  pathItemSourceId,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  skipNotes,
  children
}: NoteContextProviderProps) {
  const { me } = usePathwrightContext()
  // Define a reducer to handle actions and state updates for creating, deleting, and updating notes.
  const [noteSelection, setNoteSelection] = useReducer(reducer, {
    action: "none"
  })

  // Which notes are expanded or collapsed in the UI.
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set<number>())

  const snakeCasedContext = convertObjectKeysToSnakeCase(context)

  const [createNote] = useCreateNoteMutation()
  const [deleteNote] = useDeleteNoteMutation()
  const [updateNote] = useUpdateNoteMutation()

  const {
    notes: unfilteredNotes,
    loadMore,
    hasMore,
    loadingMore,
    query: notesQuery
  } = useNotes({
    variables: {
      context: snakeCasedContext,
      first: 1042
    },
    // Public path preview can render without note access; avoid permission errors.
    skip: !me?.id || skipNotes
  })

  // Create path from path items (nested structure), provided the path items
  // are not already in a nested structure.
  const nestedPathItems = useMemo(
    () =>
      pathItems?.[0]
        ? "items" in pathItems[0]
          ? pathItems
          : createPathFromItems(pathItems)
        : [],
    [pathItems]
  )

  // Flattened path items for easier lookup.
  const flattenedPathItems = useMemo(
    () => unnestPathItems(nestedPathItems),
    [nestedPathItems]
  )

  const groupedNotesByTarget = useGroupedNotesByTarget({
    notes: unfilteredNotes,
    pathItems: nestedPathItems
  })

  // Filter notes based on selected path item source ID, if provided.
  const notes = pathItemSourceId
    ? groupedNotesByTarget[pathItemSourceId]?.notes || unfilteredNotes
    : unfilteredNotes

  // Current note based on selection state.
  const note = useMemo(() => {
    if (
      noteSelection.action === "update" ||
      noteSelection.action === "delete"
    ) {
      return notes.find((note) => note.id === noteSelection.noteId) || null
    }

    return null
  }, [noteSelection, notes])

  // Find the currently selected path item, if any.
  const pathItem = useMemo(() => {
    const sourceId =
      pathItemSourceId ||
      note?.step_id ||
      (noteSelection.action === "create" ? noteSelection.itemId : null)

    return (
      flattenedPathItems.find((item) => item.source_id === sourceId) || null
    )
  }, [pathItemSourceId, noteSelection, note, flattenedPathItems])

  // Function to confirm the current note selection action.
  // Ideally, we'd move this to a hook, but this depends on too many
  // props and state values to make that practical.
  async function confirmSelection(noteInput?: NoteInput) {
    switch (noteSelection.action) {
      case "create":
        if (!noteInput) {
          throw new Error(
            "Note input is required to confirm creation of a note."
          )
        }

        // Temporary negative ID for optimistic note.
        const randomId = -Math.floor(Math.random() * 1000000)

        const optimisticNote: NoteFragment = {
          ...noteInput,
          id: randomId,
          context_id: randomId,
          created_time: new Date().toISOString(),
          modified_time: new Date().toISOString(),
          resource_id: snakeCasedContext.resource_id,
          cohort_id: snakeCasedContext.cohort_id,
          created_from_cohort_id: snakeCasedContext.cohort_id,
          step_id: noteSelection.itemId,
          user: me,
          __typename: "Note"
        }

        createNote({
          variables: {
            context: {
              ...context,
              path_item_source_id: noteSelection.itemId
            },
            note: noteInput
          },
          optimisticResponse: {
            createNote: optimisticNote
          },
          update: (cache, { data }) => {
            const note = data?.createNote
            if (!note || note.id === randomId) return

            // Evict the temporary optimistic entry
            cache.evict({ id: cache.identify(optimisticNote) })
            cache.gc() // Garbage collect to clean up

            onCreateNote?.(note)
          }
        })

        // Update the Apollo cache to add the created note optimistically.
        notesQuery.updateQuery((previousResult) => {
          // TS doesn't recognize { notes: { ...previousResult.notes } as being typed the same
          // as previousResult.notes, so we need to cast here.
          const nextResult = {
            ...previousResult,
            notes: { ...previousResult.notes }
          } as typeof previousResult

          if (nextResult.notes) {
            const updatedEdges = [
              { __typename: "NoteEdge", node: optimisticNote },
              ...(nextResult.notes.edges || [])
            ] as typeof nextResult.notes.edges

            nextResult.notes.edges = updatedEdges
            nextResult.notes.total = (nextResult.notes.total || 0) + 1
          }

          return nextResult
        })

        break

      case "update": {
        if (!noteInput) {
          throw new Error("Note input is required to confirm update of a note.")
        }

        const note = notes.find((note) => note.id === noteSelection.noteId)!

        updateNote({
          variables: {
            context: {
              resource_id: note.resource_id,
              cohort_id: note.cohort_id,
              path_item_source_id: note.step_id
            },
            id: noteSelection.noteId,
            note: noteInput
          },
          optimisticResponse: {
            updateNote: {
              ...note,
              ...noteInput
            }
          },
          update: (cache, { data }) => {
            const updatedNote = data?.updateNote
            if (updatedNote) {
              onUpdateNote?.(updatedNote)
            }
          }
        })

        break
      }
      case "delete": {
        const note = notes.find((note) => note.id === noteSelection.noteId)!

        deleteNote({
          variables: {
            context: {
              resource_id: note.resource_id,
              cohort_id: note.cohort_id,
              path_item_source_id: note.step_id
            },
            id: noteSelection.noteId
          },
          optimisticResponse: {
            deleteNote: true
          },
          update: (cache) => {
            // Evict the deleted note from the cache
            cache.evict({ id: cache.identify(note) })
            cache.gc() // Garbage collect to clean up
            onDeleteNote?.(note)
          }
        })

        // Update the Apollo cache to remove the deleted note optimistically.
        notesQuery.updateQuery((previousResult) => {
          // TS doesn't recognize { notes: { ...previousResult.notes } as being typed the same
          // as previousResult.notes, so we need to cast here.
          const nextResult = {
            ...previousResult,
            notes: { ...previousResult.notes }
          } as typeof previousResult

          if (nextResult.notes) {
            const updatedEdges =
              nextResult.notes.edges?.filter(
                (edge) => edge!.node!.id !== noteSelection.noteId
              ) || []

            nextResult.notes.edges = updatedEdges
            nextResult.notes.total = (nextResult.notes.total || 0) - 1
          }

          return nextResult
        })

        break
      }

      case "none":
        // No action to confirm.
        break
      default:
        exhaustiveGuard(noteSelection)
    }

    // Reset selection after confirming action.
    setNoteSelection({ action: "none" })
  }

  // Users note permissions for the scoped note, if any.
  // This may be a confusing pattern as we must use the useNotePermissions hook
  // directly to determine permissions for a specific, unselected note (i.e. in list items).
  // Otherwise, you can rely on these notePermissions in all other contexts.
  const notePermissions = useNotePermissions(snakeCasedContext, note)

  return (
    <NoteContext.Provider
      value={{
        context: snakeCasedContext,
        note,
        notes,
        notesQuery,
        loadMore,
        hasMore,
        loadingMore,
        groupedNotesByTarget,
        pathItem,
        pathItems: nestedPathItems,
        flattenedPathItems,
        pathItemSourceId: pathItemSourceId || null,
        noteSelection,
        setNoteSelection,
        expandedNoteIds,
        setExpandedNoteIds,
        confirmSelection,
        notePermissions
      }}
    >
      {children}
    </NoteContext.Provider>
  )
}
